import Scrollhistorie from '@nrk/dh-basis-scrollhistorie'
import { closest } from '@nrk/dh-basis-common'
import { easeInOutSine } from 'js-easing-functions'
import positionStyles from './content/media/shared/text-position.module.css'
import { KSScrollanimasjonMixin } from './scroll-animasjon-mixin'
import { scrollSnapSupported } from './feature-support'

const MIN_TEXT_HEIGHT = 130
/*
 * Each section is a separate "scrollhistorie" component.
 * */
export class CardSection extends KSScrollanimasjonMixin(Scrollhistorie) {
  _updatingCardVisibility = false

  /** @type {KortstokkApp} */
  #app

  connectedCallback() {
    super.connectedCallback()
    this.updateHeights(window.innerHeight)

    window.addEventListener('resize', this._handleResize)
    this._updateTextMeasurements()

    // Remeasure for iOS 15
    setTimeout(() => {
      this._updateTextMeasurements()
    }, 500)

    // TODO: Remove once Safari has support for Scroll Driven Animations
    if (!CSS.supports('animation-timeline', 'view()') && scrollSnapSupported) {
      this._updateCardVisiblePercentage()
    }
    this._setShareLinks()

    // eslint-disable-next-line compat/compat
    if (document.fonts) {
      // eslint-disable-next-line compat/compat, promise/catch-or-return
      document.fonts.ready.then(this._fitTexts)
    }

    this._printMQ = window.matchMedia('print')
    this._printMQ.addEventListener?.('change', this._handlePrintMedia)
    this._handlePrintMedia()

    this.#app = document.querySelector('kortstokk-app')
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this._handleResize)
    this._printMQ.removeEventListener?.('change', this._handlePrintMedia)
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue)

    if (name === 'viewport-position' && newValue === 'IN-VIEWPORT') {
      // TODO: Remove once Safari has support for Scroll Driven Animations
      if (!CSS.supports('animation-timeline', 'view()') && scrollSnapSupported) {
        this._updateCardVisiblePercentage()
      }
    }
  }

  updateHeights = (viewportHeight) => {
    this._innerHeight = viewportHeight
  }

  _handleResize = () => {
    if (this._resizeAF) {
      cancelAnimationFrame(this._resizeAF)
    }
    // Wait for the next animation frame to prevent reflow
    this._resizeAF = requestAnimationFrame(() => {
      const innerHeight = document.documentElement.clientHeight

      this._updateTextMeasurements()
      this._fitTexts()
      this.updateHeights(innerHeight)
      delete this._resizeAF
    })
  }

  _fitTexts = () => {
    const fitTextContainers = Array.from(this.querySelectorAll(`[data-fit-text]`))
    fitTextContainers.forEach((fitTextContainer) => {
      const propName = fitTextContainer.getAttribute('data-fit-text')
      let fontReduce = 0
      /* Safari 12 does not contain header withing column, must check if width exceeds available screen space */
      const availableWidth = Math.min(window.innerWidth - 30, fitTextContainer.offsetWidth)
      while (fitTextContainer.scrollWidth > availableWidth && fontReduce < 20) {
        fontReduce++
        this.style.setProperty(propName, `${fontReduce}px`)
      }
    })
  }

  _setShareLinks() {
    const facebookLink = this.querySelector('#facebook')
    const url = window.location.href
    if (facebookLink) {
      facebookLink.setAttribute('href', `https://www.facebook.com/sharer.php?u=${url}`)
    }

    const twitterLink = this.querySelector('#twitter')
    if (twitterLink) {
      twitterLink.setAttribute('href', `https://twitter.com/intent/tweet?url=${url}`)
    }
  }

  _updateTextMeasurements = () => {
    requestAnimationFrame(() => {
      const backgrounds = this.backgrounds
      const isDesktop = window.matchMedia('(min-aspect-ratio: 7 / 8)').matches
      const backgroundUpdates = backgrounds.map((background) => {
        /*
         * Each background has a separate gradient cover to provide contrast.
         *
         * Gradient must be tall enough to provide WCAG contrast for the tallest text element.
         * Text-elements that needs measurement is marked with .measure-text.
         * */
        const topSelector = `.${positionStyles.placeTop}`

        if (background.matches(topSelector) && !isDesktop) {
          const heights = this._getMeasureTextElementsFor(background).map((needsGradient) => {
            return needsGradient.offsetTop + needsGradient.offsetHeight
          })
          const topTextHeight = Math.max(MIN_TEXT_HEIGHT, ...heights)
          return { background, topTextHeight }
        } else {
          const heights = this._getMeasureTextElementsFor(background).map((needsGradient) => {
            return this._getCardParent(needsGradient).offsetHeight - needsGradient.offsetTop
          })
          const bottomTextHeight = Math.max(MIN_TEXT_HEIGHT, ...heights)
          return { background, bottomTextHeight }
        }
      })

      /* Update backgrounds after all measurements are completed */
      requestAnimationFrame(() => {
        for (const backgroundUpdate of backgroundUpdates) {
          const { background, topTextHeight = 0, bottomTextHeight = 0 } = backgroundUpdate

          background.style.setProperty('--text-top-height', `${topTextHeight}px`)
          background.style.setProperty('--text-bottom-height', `${bottomTextHeight}px`)

          if (background.tagName === 'DHK-BASIS-KART') {
            // If the background is a map, we need to set aside place for the text card.
            // This is done using the padding attribute.
            // As quote cards are lifted to the top on desktop, we need to set a limit this padding.
            const paddingTop = Math.min(topTextHeight, 220)
            const paddingBottom = Math.min(bottomTextHeight, 220)
            background.setAttribute('padding', `${paddingTop}px 0px ${paddingBottom} 0px`)
          }
        }
      })
    })
  }

  /**
   * Update percentage used for card opacity.
   *
   * TODO: Remove once Safari has support for Scroll Driven Animations
   * @private
   */
  _updateCardVisiblePercentage() {
    if (this._updatingCardVisibility) {
      return
    }
    this._updatingCardVisibility = true

    const cards = this.cards
    const loop = () => {
      if (!window.matchMedia('screen').matches) {
        return
      }
      if (this.viewportPosition !== 'IN-VIEWPORT') {
        this._updatingCardVisibility = false
        return
      }
      if (this._resizeAF) {
        // Skip updating card visibility while resizing as the combination will cause a reflow
        requestAnimationFrame(loop)
        return
      }

      // const scrollTop = this.parentElement.scrollTop
      const scrollTop = document?.scrollingElement.scrollTop
      const innerHeight = this._innerHeight
      const maxDistance = 0.75 * innerHeight
      const offsets = cards.map((card) => card.offsetTop)

      for (const [idx, card] of cards.entries()) {
        const distance = scrollTop - offsets[idx]
        let absDistance = Math.min(maxDistance, Math.abs(distance))
        if (idx === 0 && distance < 0) {
          /* First card in section should not fade in */
          absDistance = 0
        } else if (idx === cards.length - 1 && distance > 0) {
          /* Last card in section should not fade out */
          absDistance = 0
        }
        const nextOpacity = easeInOutSine(absDistance, 1, -0.9, maxDistance).toFixed(2)

        for (const textElement of card.querySelectorAll('.fade-text')) {
          const currentOpacity = textElement.style.getPropertyValue('--dh-fade-opacity')
          if (currentOpacity !== nextOpacity) {
            textElement.style.setProperty('--dh-fade-opacity', nextOpacity)
          }

          /*
            Layer optimization:
            Set will-change to opacity only if card is near the screen
            */
          if (Math.abs(distance) > 2 * innerHeight) {
            if (textElement.style.willChange === 'opacity') {
              textElement.style.removeProperty('will-change')
            }
          } else {
            if (textElement.style.willChange !== 'opacity') {
              textElement.style.setProperty('will-change', 'opacity')
            }
          }
        }
      }
      requestAnimationFrame(loop)
    }

    requestAnimationFrame(loop)
  }

  _getMeasureTextElementsFor(background) {
    const backgroundId = background.getAttribute('data-background-id')
    return Array.from(
      this.querySelectorAll(`[data-show-background="${backgroundId}"] .measure-text`),
    )
  }

  _getCardParent(element) {
    return closest(element, '[data-show-background]')
  }

  _handlePrintMedia = () => {
    if (window.matchMedia('print').matches) {
      for (const image of this.querySelectorAll('img')) {
        image.setAttribute('loading', 'eager')
      }
    }
  }

  get cards() {
    return Array.from(this.querySelectorAll('[data-show-background]'))
  }
}
